import { Inject } from '@odinjs/odin';

import { Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick } from '@philips-emr/hpms-provider-accreditation-components';
import { ActionAPI, FetchAPI, FieldAPI, FormAPI, LocatorAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnBeforeFetch, OnBlur, OnCancel, OnChange, OnClick, OnClose, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import './provider-accreditation-specialty-controller.css';
import './accreditation-token.css';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { ProviderAccreditationProceduresAliases as Aliases } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationSteps } from './provider-accreditation-controller';

interface Procedure {
  nrSequence?: number,
  cdSpecialty?: number,
  locatorProcedure?: TypeProcedure,
  vlProcedure: number,
  vlMedical: number,
  vlFilm: number,
  vlOperationalCost: number,
  procedureValue?: number,
}

type TypeProcedure = {
  cdProcedure?: number,
  dsProcedure?: string,
  ieSourceProcedure?: number,
};

type MyException = {
  body: string,
};

const OptionKey = {
  EDIT: 'edit',
  DELETE: 'delete',
};

@Controller({ fragment: 'provider-accreditation-procedures' })
class ProviderAccreditationProceduresController extends BaseController {

  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;
  @Inject(Aliases.FETCH_LIST)
  private fetchList: FetchAPI;
  @Inject(Aliases.FETCH_PROCEDURE)
  private fetchProcedure: FetchAPI;
  @Inject(Aliases.FORM_PROCEDURE)
  private form: FormAPI;
  @Inject(Aliases.LOCATOR_PROCEDURE)
  private locator: LocatorAPI;
  @Inject(Aliases.FIELD_VALUE_PROCEDURE)
  private fieldValueProcedure: FieldAPI;
  @Inject(Aliases.FIELD_OPERATIONAL_COST)
  private fieldOperationalCost: FieldAPI;
  @Inject(Aliases.FIELD_MEDICAL_VALUE)
  private fieldMedicalValue: FieldAPI;
  @Inject(Aliases.FIELD_FILM_VALUE)
  private fieldFilmValue: FieldAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.FIELD_ORIGIN)
  private fieldOrigin: FieldAPI;

  @OnClick(Aliases.BUTTON_ADD_PROCEDURE)
  onClickAdd(): void {
    const { nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqTypeAccreditation: nrSeqTypeAccreditation });
    this.context.set({ nrSequence: null });
    this.panelModal.setVisible(true);
  }

  @OnClose(Aliases.MODAL_ADD_NEW)
  onCloseModal(): void {
    this.cleanForm();
    this.panelModal.setVisible(false);
    this.refreshList();
  }
  private refreshList(): void {
    const cdSpecialty = this.context.get('cdSpecialty');
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.fetchList.setParameters({ nrSeqUser, nrSeqAccreditation, cdSpecialty });
    this.fetchList.forceFetch();
  }

  @OnChange(Aliases.FIELD_SPECIALTY)
  onChangeSpecialty(event: ChangeEvent<string>): void {
    this.context.set({ cdSpecialty: event.value });
  }

  @OnBeforeFetch(Aliases.FETCH_PROCEDURE)
  onBeforeFetchProcedure(): void {
    const nrSequence = this.context.get('nrSequence');
    const { nrSeqUser, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqTypeAccreditation: nrSeqTypeAccreditation });
    this.fetchProcedure.setParameters({ nrSeqUser: nrSeqUser, nrSequence: nrSequence });
  }

  @OnBeforeFetch(Aliases.FETCH_LIST)
  onBeforeFetchList(): void {
    const fetchParams = this.fetchList.getParameters();
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.fetchList.setParameters({ ...fetchParams, nrSeqAccreditation });
  }
  @OnAfterFetch(Aliases.FETCH_PROCEDURE)
  onAfterFetchProcedure(): void {
    const nrSequence = this.context.get('nrSequence');
    this.locator.setReadOnly(!!nrSequence);
  }

  private async saveRecord(): Promise<void> {
    const record = this.form.getRecord() as unknown as Procedure;
    const cdSpecialty = this.context.get('cdSpecialty');
    const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    const { locatorProcedure } = record;
    await HttpService.post(Endpoint.POST.PROCEDURES, { nrSeqUser }, {
      body: {
        ...record, cdProcedure: locatorProcedure?.cdProcedure,
        ieSourceProcedure: locatorProcedure?.ieSourceProcedure, nrSeqAccreditation: nrSeqAccreditation, cdSpecialty: cdSpecialty,
      },
    }).then(() => {
      this.cleanForm();
      this.refreshList();
      this.panelModal.setVisible(false);
    }).catch(({ body }) => ToastServiceProviderAccreditation.error(MessageService.translate(body)));
  }

  @HPMSOnOptionClick(Aliases.MY_DATA_CARD)
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSequence } = data as unknown as Procedure;

    const action = {
      [OptionKey.EDIT]: () => this.editRecord(Number(nrSequence)),
      [OptionKey.DELETE]: () => this.deleteRecord(Number(nrSequence)),
    };

    if (action[key]) {
      action[key]();
    }
  }

  private editRecord(nrSequence: number): void {
    this.context.set({ nrSequence: nrSequence });
    this.panelModal.setVisible(true);
    this.fetchProcedure.forceFetch();
  }

  private async deleteRecord(nrSequence: number): Promise<void> {
    try {
      const { nrSeqUser, nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
      await HttpService.delete(Endpoint.DELETE.PROCEDURES, { nrSeqUser, nrSequence }, { body: { nrSeqAccreditation: nrSeqAccreditation } });
      this.refreshList();

    } catch (e) {
      const { body } = e as MyException;
      ToastServiceProviderAccreditation.error(MessageService.translate(body));
    }
  }

  @OnCancel(Aliases.FORM_PROCEDURE)
  onCleanForm(): void {
    this.cleanForm();
  }

  @OnSubmit(Aliases.FORM_PROCEDURE)
  async onSaveActionClick(): Promise<void> {
    await this.saveRecord();
  }

  @OnBlur([
    Aliases.FIELD_OPERATIONAL_COST, Aliases.FIELD_MEDICAL_VALUE,
    Aliases.FIELD_FILM_VALUE])
  onChangeValues(): void {
    const { vlOperationalCost } = this.fieldOperationalCost.getData() as unknown as Procedure;
    const { vlMedical } = this.fieldMedicalValue.getData() as unknown as Procedure;
    const { vlFilm } = this.fieldFilmValue.getData() as unknown as Procedure;

    const total = vlOperationalCost + vlMedical + vlFilm;
    this.fieldValueProcedure.setValue(total);
  }

  @OnMount(Aliases.BUTTON_ADD_PROCEDURE)
  onMountPanelForm(event: MountEvent): void {
    const api = event.target as ActionAPI;
    FormViewService.shouldRenderAction(api, AccreditationSteps.PROCEDURES);
  }

  @OnChange(Aliases.FIELD_CODE)
  onChangeCode(event: ChangeEvent<string>): void {
    const cdProcedure = event.value;
    if (cdProcedure && cdProcedure.length > 0) {
      this.fieldOrigin.setValidators([{ name: 'required', options: {} }] as Validator[]);
    } else {
      this.fieldOrigin.setValidators([]);
    }
  }

  @HPMSOnCreateMenu(Aliases.MY_DATA_CARD)
  onCreateMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { editable } = data;

    if (editable == 'N') {
      shouldRender(false);
    }
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.PROCEDURES);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  private cleanForm(): void {
    this.form.setValue({});
    this.locator.setReadOnly(false);
    this.context.set({ nrSequence: null });
  }
}

export {
  ProviderAccreditationProceduresController,
};
